/*
 * search-strict-query.js — every theme search lands on strict results (B2C-349)
 *
 * Shopify's online-store search adds semantic matches, and no setting switches them
 * off: "gelato" returned 561 products, most of them unrelated. Search syntax switches
 * them off for that query, and the one form that keeps every typed word required and
 * adds nothing else is each word in double quotes ("gelato" 25, "frozen" "pizza" 15;
 * measured on production, AE market, 2026-10-23). Plan: plans/search-strict-query.md.
 *
 * - strictQuery(q) quotes each whitespace-separated word. A query that already uses
 *   syntax comes back unchanged, because the shopper meant it: a double quote, a colon
 *   (field search), parentheses, `*`, a word starting with `-`, or AND / OR / NOT as
 *   an uppercase word.
 * - Forms opt in with data-search-strict (header, mobile overlay, Cmd+K). The submit
 *   listener sits on document, so it runs after the forms' own listeners and the
 *   empty-query guards (B2C-321) still win: a defaultPrevented submit is left alone.
 *   It navigates itself and never writes the input, so Back never shows the quotes.
 * - Query-suggestion pills opt in with data-search-strict-link
 *   (sections/predictive-search.liquid). Their href is rewritten at click time, so a
 *   modified or middle click opens the strict URL too.
 * - Two call sites build their URL in JS and read window.CasinettoSearch at event
 *   time: the Enter branch in predictive-search.js and the mobile overlay's "View all
 *   results" link (sections/casinetto-header.liquid).
 * - Not rewritten: direct links, the "Recently viewed" `id:` request, the structured-
 *   data SearchAction, and the results page's broad-search link and fallback redirect
 *   (sections/search-results.liquid, sections/casinetto-search-header.liquid).
 *
 * Exposes: window.CasinettoSearch = { strictQuery, searchUrl }
 * Emits and listens to no custom events.
 */
(function () {
  'use strict';

  var SYNTAX = /["():*]|(^|\s)-|\b(AND|OR|NOT)\b/;

  function strictQuery(value) {
    var q = typeof value === 'string' ? value.trim() : '';
    if (!q || SYNTAX.test(q)) return value;

    return q.split(/\s+/).map(function (word) {
      return '"' + word + '"';
    }).join(' ');
  }

  // The strict results URL every entry point lands on, locale-aware.
  function searchUrl(value) {
    var routes = window.Theme && window.Theme.routes;
    var url = new URL((routes && routes.search_url) || '/search', window.location.origin);
    url.searchParams.set('q', strictQuery(value));
    url.searchParams.set('options[prefix]', 'last');
    return url.toString();
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-search-strict')) return;
    if (event.defaultPrevented) return;

    var input = form.elements.namedItem('q');
    if (!(input instanceof HTMLInputElement)) return;

    var q = input.value.trim();
    if (!q) return;

    var strict = strictQuery(q);
    if (strict === q) return;

    event.preventDefault();
    var data = new FormData(form);
    data.set('q', strict);
    data.set('options[prefix]', 'last');
    var url = new URL(form.action, window.location.origin);
    url.search = new URLSearchParams(data).toString();
    window.location.assign(url.toString());
  });

  function rewriteLink(event) {
    var link = event.target instanceof Element ? event.target.closest('a[data-search-strict-link]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    var url = new URL(link.href, window.location.origin);
    var q = url.searchParams.get('q');
    if (!q) return;

    var strict = strictQuery(q);
    if (strict === q) return;

    url.searchParams.set('q', strict);
    url.searchParams.set('options[prefix]', 'last');
    link.href = url.toString();
  }

  document.addEventListener('click', rewriteLink);
  document.addEventListener('auxclick', rewriteLink);

  window.CasinettoSearch = { strictQuery: strictQuery, searchUrl: searchUrl };
})();
